/**
 * BuyerPreviewModal — Sprint M
 *
 * Modal that shows the exact buyer overview map for the current in-progress
 * template, sourced from live template data via
 * GET /api/templates/{id}/preview-as-buyer (no event attach required).
 */
import React, { useEffect, useState } from "react";
import { Eye, Loader2, X } from "lucide-react";
import { venuesApi } from "../../lib/events";
import VenueOverviewMap from "../VenueOverviewMap";
import { PricingLegendInline } from "../PricingLegend";
import { toast } from "sonner";
import { extractError } from "../../lib/api";

export default function BuyerPreviewModal({ templateId, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!open || !templateId) return;
    let alive = true;
    setLoading(true);
    venuesApi
      .previewAsBuyer(templateId)
      .then((p) => { if (alive) setPayload(p); })
      .catch((err) => { if (alive) toast.error(extractError(err)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, templateId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Buyer preview"
      data-testid="buyer-preview-modal"
    >
      <button
        type="button"
        aria-label="Close preview"
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-white rounded-sm shadow-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Eye size={14} className="text-[var(--gt-accent)]" />
            <div className="font-heading text-xl font-black tracking-tight">Buyer preview</div>
            <span className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">Not published · live template data</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-sm border border-[var(--gt-line)] flex items-center justify-center hover:bg-[var(--gt-bg-surface)]"
            data-testid="buyer-preview-close"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {loading && (
          <div className="p-8 flex justify-center" data-testid="buyer-preview-loading">
            <Loader2 size={20} className="animate-spin text-[var(--gt-ink-3)]" />
          </div>
        )}

        {!loading && payload && (
          <>
            {!payload.has_section_geometry && (
              <p className="text-xs text-[var(--gt-ink-3)] mb-3">
                No section geometry yet. Add rectangles or wedges to the canvas to see the overview map.
              </p>
            )}
            {payload.has_section_geometry && (
              <>
                <VenueOverviewMap
                  sections={payload.sections}
                  zones={payload.zones}
                  availability={payload.availability}
                  onSelectSection={() => {}}
                  viewportWidth={payload.layout?.viewport_width || 100}
                  viewportHeight={payload.layout?.viewport_height || 100}
                />
                <PricingLegendInline zones={payload.zones} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
